import type { Style } from './style';
import type { Value } from './value';

export type NodeId = string;
export type LayerId = string;

export type EntityType = 'line' | 'circle' | 'arc';
export type DimensionKind = 'linear' | 'radius' | 'diameter' | 'angle' | 'callout';
export type SceneNodeType = 'group' | EntityType | 'dimension' | 'view';

export interface PointValue {
  readonly x: Value;
  readonly y: Value;
}

export interface NodeBase {
  readonly id: NodeId;
  readonly name: string;
  readonly parentId: NodeId | null;
  readonly visible: boolean;
  readonly locked: boolean;
}

/** Groups are structure only; layers belong to the entities and dimensions inside them. */
export interface EntityBase extends NodeBase {
  readonly layerId: LayerId;
  /** Only the properties this entity overrides; the rest come from its layer. */
  readonly style?: Style;
}

export interface GroupNode extends NodeBase {
  readonly type: 'group';
  readonly childIds: readonly NodeId[];
}

export interface LineNode extends EntityBase {
  readonly type: 'line';
  readonly a: PointValue;
  readonly b: PointValue;
}

export interface CircleNode extends EntityBase {
  readonly type: 'circle';
  readonly center: PointValue;
  readonly radius: Value;
}

export interface ArcNode extends EntityBase {
  readonly type: 'arc';
  readonly center: PointValue;
  readonly radius: Value;
  readonly startAngle: Value;
  readonly endAngle: Value;
}

export type EntityNode = LineNode | CircleNode | ArcNode;

export interface XY {
  readonly x: number;
  readonly y: number;
}

/**
 * An annotation measuring geometry, as the script left it: plain numbers, never `Value`s,
 * since nothing edits a dimension but its script.
 */
interface DimensionBase extends EntityBase {
  readonly type: 'dimension';
}

/** The distance from `a` to `b` along `angle`, drawn `offset` to the left of that direction. */
export interface LinearDimensionNode extends DimensionBase {
  readonly kind: 'linear';
  readonly a: XY;
  readonly b: XY;
  readonly angle: number;
  /** Null places it automatically, from the text height. */
  readonly offset: number | null;
}

/** A circle's radius or diameter, drawn through its center at `angle`. */
/** Where a radius or diameter puts its text; recorded as its index plus one, 0 for neither. */
export const ROUND_TEXTS = ['inside', 'outside'] as const;

export type RoundText = (typeof ROUND_TEXTS)[number];

export interface RoundDimensionNode extends DimensionBase {
  readonly kind: 'radius' | 'diameter';
  readonly center: XY;
  readonly radius: number;
  readonly angle: number;
  /** Where its text goes; null puts it inside when it fits, else outside. */
  readonly text: RoundText | null;
}

/** The angle between two lines, from `first` counter-clockwise to `second`. */
export interface AngleDimensionNode extends DimensionBase {
  readonly kind: 'angle';
  readonly first: { readonly a: XY; readonly b: XY };
  readonly second: { readonly a: XY; readonly b: XY };
  /** Null places the arc automatically, inside the shorter line. */
  readonly radius: number | null;
}

/**
 * A label on an object: a plain leader from `anchor` out along `angle`, `length` long, with
 * `label` riding the leader past its end.
 */
export interface CalloutDimensionNode extends DimensionBase {
  readonly kind: 'callout';
  readonly anchor: XY;
  readonly angle: number;
  /** Null makes it two text heights long. */
  readonly length: number | null;
  readonly label: string;
}

export type DimensionNode =
  LinearDimensionNode | RoundDimensionNode | AngleDimensionNode | CalloutDimensionNode;

/**
 * A named frame around what a script drew inside it, `margin` clear of it, titled with its
 * name. Its members keep their own layers; the frame and title use the view's.
 */
export interface ViewNode extends EntityBase {
  readonly type: 'view';
  readonly margin: number;
  readonly memberIds: readonly NodeId[];
}

export type SceneNode = GroupNode | EntityNode | DimensionNode | ViewNode;
/** What sits on a layer: entities, dimensions and views. */
export type LayeredNode = EntityNode | DimensionNode | ViewNode;

export const isGroup = (node: SceneNode): node is GroupNode => node.type === 'group';
export const isDimension = (node: SceneNode): node is DimensionNode => node.type === 'dimension';
export const isView = (node: SceneNode): node is ViewNode => node.type === 'view';
export const isEntity = (node: SceneNode): node is EntityNode =>
  node.type !== 'group' && node.type !== 'dimension' && node.type !== 'view';
export const isLayered = (node: SceneNode): node is LayeredNode => node.type !== 'group';

/** Reads a one- or two-level field path such as `radius` or `center.x`. */
export function readPath(source: object, path: string): unknown {
  const [head, tail] = path.split('.') as [string, string | undefined];
  const outer = (source as Record<string, unknown>)[head];
  if (tail === undefined || outer === undefined || outer === null) return outer;
  return (outer as Record<string, unknown>)[tail];
}

export function getField(node: SceneNode, path: string): Value | undefined {
  return readPath(node, path) as Value | undefined;
}

export function setField<T extends SceneNode>(node: T, path: string, value: Value): T {
  const [head, tail] = path.split('.') as [string, string | undefined];
  if (tail === undefined) return { ...node, [head]: value };
  const outer = (node as unknown as Record<string, unknown>)[head] as Record<string, unknown>;
  return { ...node, [head]: { ...outer, [tail]: value } };
}
